import React from 'react';
import { MapPin, Car, Accessibility, Video, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface EventVenueSectionProps {
  location: string;
  hasLivestream?: boolean;
}

const EventVenueSection: React.FC<EventVenueSectionProps> = ({ 
  location,
  hasLivestream = true 
}) => {
  const venueFeatures = [
    { icon: Car, label: 'Free parking available' },
    { icon: Accessibility, label: 'Wheelchair accessible' },
  ];

  // Create Google Maps link from location
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;

  return (
    <section className="space-y-6 print:break-inside-avoid">
      <div>
        <p className="text-sm font-medium text-primary uppercase tracking-wider mb-2">
          Where We Gather
        </p>
        <h2 className="text-2xl md:text-3xl font-bold font-display">
          Venue & Access
        </h2>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* In-Person Venue */}
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold font-display">In-Person</h3>
                <p className="text-muted-foreground">{location}</p>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              {venueFeatures.map((feature) => {
                const IconComponent = feature.icon;
                return (
                  <div key={feature.label} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <IconComponent className="h-4 w-4 text-secondary" />
                    <span>{feature.label}</span>
                  </div>
                );
              })}
            </div>

            <Button variant="outline" size="sm" asChild className="print:hidden">
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                Open in Maps
                <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </a>
            </Button>
            
            {/* Print-only URL display */}
            <p className="hidden print:block text-xs text-muted-foreground mt-2 break-all">
              Maps: {mapsUrl}
            </p>
          </CardContent>
        </Card>

        {/* Livestream */}
        {hasLivestream && (
          <Card className="bg-muted/20 border-border">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center shrink-0">
                  <Video className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <h3 className="font-semibold font-display">Can't Make It In Person?</h3>
                  <p className="text-muted-foreground">Join us via livestream</p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-4">
                We'll be streaming live across YouTube, Twitch, and other platforms. 
                Register for virtual access to get notified when we go live.
              </p>

              <div className="flex flex-wrap gap-2">
                <span className="text-xs px-2 py-1 rounded bg-background border border-border">YouTube</span>
                <span className="text-xs px-2 py-1 rounded bg-background border border-border">Twitch</span>
                <span className="text-xs px-2 py-1 rounded bg-background border border-border">+ More</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
};

export default EventVenueSection;
